import { useEffect, useState } from 'react'
import { useTheme } from '@/stores/local-settings.store'

export type ResolvedTheme = 'light' | 'dark'

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)'

// matchMedia is absent during SSR and in some test environments; treat a
// missing implementation as "no dark preference" rather than throwing.
function darkSchemeQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia(DARK_SCHEME_QUERY)
}

/**
 * The concrete light/dark value behind the stored theme preference. Resolved
 * from the preference plus the media query - never from the `dark` class on
 * the document, which `useThemeEffect` writes a frame later.
 */
export function useResolvedTheme(): ResolvedTheme {
  const theme = useTheme()
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => darkSchemeQuery()?.matches ?? false)

  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = darkSchemeQuery()
    if (!mediaQuery) return
    setSystemPrefersDark(mediaQuery.matches)
    const handleChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  if (theme === 'system') return systemPrefersDark ? 'dark' : 'light'
  return theme === 'dark' ? 'dark' : 'light'
}
